import { z } from 'zod';

export const paymentStatusSchema = z.enum([
  'draft',
  'pending_approval',
  'approved',
  'processing',
  'completed',
  'failed',
]);

export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

export const paymentMethodSchema = z.enum([
  'bank_transfer',
  'wire',
  'ach',
  'check',
]);

export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

export const paymentItemSchema = z.object({
  invoiceId: z.string(),
  invoiceNumber: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  amount: z.number(),
  currency: z.string(),
  dueDate: z.string(),
});

export type PaymentItem = z.infer<typeof paymentItemSchema>;

export const paymentBatchSchema = z.object({
  batchId: z.string(),
  batchNumber: z.string(),
  status: paymentStatusSchema,
  paymentMethod: paymentMethodSchema,
  currency: z.string(),
  totalAmount: z.number(),
  invoiceCount: z.number(),
  items: z.array(paymentItemSchema),
  scheduledDate: z.string(),
  processedDate: z.string().nullable(),
  createdBy: z.string(),
  approvedBy: z.string().nullable(),
  notes: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PaymentBatch = z.infer<typeof paymentBatchSchema>;

export const paymentBatchesResponseSchema = z.object({
  data: z.array(paymentBatchSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number(),
  }),
  meta: z.object({
    totalPending: z.number(),
    totalProcessing: z.number(),
    totalCompleted: z.number(),
  }),
});

export type PaymentBatchesResponse = z.infer<typeof paymentBatchesResponseSchema>;

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
};

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  bank_transfer: 'Bank Transfer',
  wire: 'Wire Transfer',
  ach: 'ACH',
  check: 'Check',
};
